'use client';

import React, { useState, useEffect } from 'react';
import { Calendar, Car, MapPin, ClipboardList, CheckCircle2, AlertCircle, CalendarCheck } from 'lucide-react';
import WorkRequestForm from './WorkRequestForm';
import WorkRequestsList from './WorkRequestsList';

export interface WorkRequest {
  id: string;
  vehicleReg: string;
  branch: string;
  taskDetails: string;
  scheduledDate: string;
  scheduledTime: string;
  calendarEventId?: string;
  calendarEventLink?: string;
  cosmeticItems: string[];
  status: 'pending' | 'synced' | 'failed';
  createdAt: string;
}

export default function WorkRequestsClient() {
  const [requests, setRequests] = useState<WorkRequest[]>([
    {
      id: 'wr-1001',
      vehicleReg: 'GP 23-441',
      branch: 'Sandton North',
      taskDetails: 'Full service — oil change, filter replacement, brake inspection',
      scheduledDate: '2026-05-20',
      scheduledTime: '08:00',
      cosmeticItems: ['Scratch on rear bumper', 'Cracked side mirror'],
      status: 'synced',
      calendarEventLink: '#',
      createdAt: '2026-05-17',
    },
    {
      id: 'wr-1002',
      vehicleReg: 'WC 45-789',
      branch: 'Cape Town Central',
      taskDetails: 'Gearbox inspection and fluid top-up',
      scheduledDate: '2026-05-21',
      scheduledTime: '10:00',
      cosmeticItems: [],
      status: 'pending',
      createdAt: '2026-05-17',
    },
  ]);

  const [submitState, setSubmitState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [lastCreatedEvent, setLastCreatedEvent] = useState<{ link?: string; cosmeticCount: number } | null>(null);

  const handleSubmit = async (formData: {
    vehicleReg: string;
    branch: string;
    taskDetails: string;
    scheduledDate: string;
    scheduledTime: string;
  }) => {
    setSubmitState('loading');
    setSubmitMessage('');
    setLastCreatedEvent(null);

    try {
      const response = await fetch('/api/work-requests/create-calendar-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create calendar event');
      }

      const newRequest: WorkRequest = {
        id: `wr-${Date.now()}`,
        vehicleReg: formData.vehicleReg,
        branch: formData.branch,
        taskDetails: formData.taskDetails,
        scheduledDate: formData.scheduledDate,
        scheduledTime: formData.scheduledTime,
        cosmeticItems: data.cosmeticItems || [],
        status: data.eventId ? 'synced' : 'pending',
        calendarEventId: data.eventId,
        calendarEventLink: data.eventLink,
        createdAt: new Date().toISOString().split('T')[0],
      };

      setRequests((prev) => [newRequest, ...prev]);
      setLastCreatedEvent({ link: data.eventLink, cosmeticCount: (data.cosmeticItems || []).length });
      setSubmitState('success');
      setSubmitMessage(
        data.eventId
          ? `Work request scheduled and synced to Google Calendar.${(data.cosmeticItems || []).length > 0 ? ` ${data.cosmeticItems.length} cosmetic item(s) appended as notes.` : ''}`
          : 'Work request saved. Calendar sync unavailable — check API configuration.'
      );
    } catch (err: unknown) {
      setSubmitState('error');
      setSubmitMessage(err instanceof Error ? err.message : 'An unexpected error occurred.');
    }
  };

  return (
    <div className="max-w-screen-xl mx-auto space-y-6 fade-in">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Work Requests</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Schedule vehicle work — auto-syncs to Google Calendar with cosmetic notes
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg card-surface border border-border">
          <CalendarCheck size={14} className="text-primary" />
          <span className="text-xs text-muted-foreground">Google Calendar Connected</span>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Requests', value: requests.length, icon: ClipboardList, color: 'text-primary' },
          { label: 'Synced', value: requests.filter((r) => r.status === 'synced').length, icon: CheckCircle2, color: 'text-positive' },
          { label: 'Pending', value: requests.filter((r) => r.status === 'pending').length, icon: Calendar, color: 'text-warning' },
          { label: 'With Cosmetic Notes', value: requests.filter((r) => r.cosmeticItems.length > 0).length, icon: Car, color: 'text-accent' },
        ].map((stat) => (
          <div key={`stat-${stat.label}`} className="card-surface rounded-xl p-4 border border-border flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
              <stat.icon size={16} className={stat.color} />
            </div>
            <div>
              <p className="text-xl font-bold text-foreground">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Main grid: form + list */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Form panel */}
        <div className="xl:col-span-2">
          <div className="card-surface rounded-xl border border-border p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <Calendar size={18} className="text-primary" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground">New Work Request</h2>
                <p className="text-xs text-muted-foreground">Fill in details to schedule & sync</p>
              </div>
            </div>

            <WorkRequestForm
              onSubmit={handleSubmit}
              isLoading={submitState === 'loading'}
            />

            {/* Submission feedback */}
            {submitState !== 'idle' && submitState !== 'loading' && (
              <div
                className={`mt-4 flex items-start gap-3 p-3 rounded-lg border text-sm ${
                  submitState === 'success' ?'bg-positive/10 border-positive/20 text-positive' :'bg-destructive/10 border-destructive/20 text-destructive'
                }`}
              >
                {submitState === 'success' ? (
                  <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p>{submitMessage}</p>
                  {submitState === 'success' && lastCreatedEvent?.link && lastCreatedEvent.link !== '#' && (
                    <a
                      href={lastCreatedEvent.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-1 text-xs underline opacity-80 hover:opacity-100"
                    >
                      <CalendarCheck size={12} />
                      View in Google Calendar
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* How it works */}
          <div className="mt-4 card-surface rounded-xl border border-border p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">How it works</p>
            <div className="space-y-2.5">
              {[
                { icon: ClipboardList, text: 'Fill in vehicle reg, branch, task details and date' },
                { icon: Car, text: 'System checks cosmetic checklist for that registration' },
                { icon: Calendar, text: 'Google Calendar event created with cosmetic items as notes' },
                { icon: MapPin, text: 'Branch location is set as the event location' },
              ].map((step, i) => (
                <div key={`step-${i}`} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <step.icon size={11} className="text-primary" />
                  </div>
                  <p className="text-xs text-muted-foreground">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Requests list */}
        <div className="xl:col-span-3">
          <WorkRequestsList requests={requests} />
        </div>
      </div>
    </div>
  );
}

const SubmissionResult: React.FC = () => {
  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.warn('Placeholder: SubmissionResult is not implemented yet.');
  }, []);
  return (
    <div>
      {/* SubmissionResult placeholder */}
    </div>
  );
};

export { SubmissionResult };